/**
 * Survey problem block renderer.
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';

import { useAsterController } from "context";
import { VButton } from "form";
import { getModuleIdFromCourseId, getQuestionMapFromIndex } from "utils/grade.util";
import ChoiceResponseRender from "./ChoiceResponseRender";
import EquationRender from "./EquationRender";
import MultipleChoiceResponseRender from "./MultipleChoiceResponseRender";
import NumericalResponseRender from "./NumericalResponseRender";
import OptionResponseRender from "./OptionResponseRender";
import PRender from "./PRender";
import StringResponseRender from "./StringResponseRender";

/**
 * Collects survey answers from OLX response tags and emails them.
 *
 * @param {{ html: string, id: string }} props
 */
const ProblemSurvey = (props) => {
  const { rawText, grade, courseId, blockId, onSubmitProblem, onContinueClick } = props; // Problem block and it's grade

  const { t } = useTranslation('common');

  const [controller] = useAsterController();
  const { user } = controller;

  const [tags, setTags] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [touchedQuizes, setTouchedQuizes] = useState([]);

  useEffect(() => {
    if (tags && tags.length) {
      const gradedIds = tags.filter(t => t.grade !== null).map(m => m.grade['id']);
      setTouchedQuizes(gradedIds.map(g => ({ 'id': g, 'state': false })));
    }
  }, [tags]);

  useEffect(() => {
    const parser = new DOMParser();
    const xml = parser.parseFromString(rawText, 'text/xml');
    const questionList = ['optionresponse', 'multiplechoiceresponse', 'choiceresponse', 'numericalresponse', 'stringresponse'];
    const items = xml.querySelector('problem') ? xml.querySelector('problem').children : [];

    // Map tag to its corresponding grade and set state
    let mappedItems = [];
    let qIndex = 0;
    for (let i = 0; i < items.length; i++) {
      const tagName = items[i].tagName ? items[i].tagName.toLowerCase() : null;
      if (!tagName) continue;

      // Map grade to its corresponding question tag ignoring other tags
      if (questionList.indexOf(tagName) > -1) {
        if (!grade || !grade.state || !grade.state.question_map) {
          // If there is no grade for this problem block, then create new one
          mappedItems.push({
            'tag': items[i],
            'grade': {
              'id': `${blockId}_${qIndex + 2}_1`,
              'correctness': null,
              'student_answer': null
            }
          });
        } else {
          mappedItems.push({ 'tag': items[i], 'grade': getQuestionMapFromIndex(grade.state?.question_map, qIndex) });
        }
        qIndex++;
      } else {
        mappedItems.push({ 'tag': items[i], 'grade': null });
      }
    }
    setTags(mappedItems);

    // Set answer state
    setAnswers(grade ? grade.state.question_map : []);

  }, [rawText, grade, blockId]);

  /**
   * Upserts a survey answer in local state.
   *
   * @param {Object} ans Answer payload.
   * @returns {void}
   */
  const onSetAnswer = (ans) => {
    setTouchedQuizes(quizes => {
      const qIdx = quizes.findIndex(q => q.id === ans.id);
      if (qIdx >= 0) quizes[qIdx]['state'] = true;
      return quizes;
    });

    let tmp = [...answers];
    const itemIndex = tmp.findIndex(t => t.id === ans.id);
    itemIndex > -1 ? tmp[itemIndex] = ans : tmp.push(ans)

    setAnswers(tmp);
  };

  /**
   * Sends completed survey answers by email.
   *
   * @param {React.FormEvent} e Submit event.
   * @returns {void}
   */
  const onSubmit = (e) => {
    e.preventDefault();

    const answersToSend = answers.filter(ans => Object.keys(ans).length === 3);
    if (!answersToSend.length) return;

    const payload = {
      'student_id': user.id,
      'course_id': courseId,
      'module_id': grade ? grade.module_id : getModuleIdFromCourseId(courseId, blockId),
      'raw_possible': tags.filter(t => t.grade).length,
      'choices': answersToSend
    };
    onSubmitProblem(payload);
    onContinueClick();
  };

  return <>
    <form onSubmit={onSubmit}>
      {
        tags.map((t, key) => {
          const tag = t.tag ? t.tag.tagName : null;
          const tagName = tag ? tag.toLowerCase() : '';
          // const tagName = t.tag.tagName.toLowerCase();
          if (tagName === 'p1' || tagName === 'label') {
            return (
              <PRender tag={t.tag} key={key} pkey={key} />
            );
          } else if (tagName === 'optionresponse') {
            return (
              <OptionResponseRender // Select box
                key={key} pkey={key}
                tag={t.tag} grade={t.grade}
                onSetAnswer={onSetAnswer}
                isShowMark={false}
                setShowMark={() => { }}
              />
            );

          } else if (tagName === 'multiplechoiceresponse') {
            return (
              <MultipleChoiceResponseRender  // Radio
                key={key} pkey={key} blockId={blockId}
                tag={t.tag} grade={t.grade}
                onSetAnswer={onSetAnswer}
                isShowMark={false}
                setShowMark={() => { }} />
            );
          } else if (tagName === 'choiceresponse') {
            return (
              <ChoiceResponseRender  // Checkbox
                key={key} pkey={key} blockId={blockId}
                tag={t.tag} grade={t.grade}
                onSetAnswer={onSetAnswer}
                isShowMark={false}
                setShowMark={() => { }}
              />
            );
          } else if (tagName === 'numericalresponse') {
            return (
              <NumericalResponseRender
                key={key} pkey={key}
                tag={t.tag} grade={t.grade}
                onSetAnswer={onSetAnswer}
                isShowMark={false}
                setShowMark={() => { }}
              />
            );
          } else if (tagName === 'stringresponse') {
            return (
              <StringResponseRender
                key={key} pkey={key}
                tag={t.tag} grade={t.grade}
                onSetAnswer={onSetAnswer}
                isShowMark={false}
                setShowMark={() => { }}
              />
            );
          } else if (tagName === 'center' || tagName === 'p') {
            return (
              <EquationRender key={key} tag={t.tag} />
            );
          }
          return <>Undefined</>;
        })
      }
      <hr className="mt-5 mb-4 color-main2" />
      <div className="w-full flex mb-3 justify-center mt-4">
        <VButton type="submit" className="mr-5" onClick={() => { }} disabled={touchedQuizes.some(t => !t.state)}>
          {t('Submit')}
        </VButton>
      </div>
    </form>
  </>;
};

export default ProblemSurvey;